<template>
  <div v-if="pending" class="flex h-[80vh] items-center justify-center">
    <CommonLoading />
  </div>
  <div v-else>
    <SubscriptionsHero
      v-if="!config.public.site.allowPurchase"
      @showCallMeModal="showCallMe = true"
    />
    <div id="papers" />
    <div class="container my-8 md:my-16 pt-8">
      <h3 class="headline-m mb-4">
        {{ $t('chooseYourPapers') }}
      </h3>
      <div class="grid md:grid-cols-3 gap-16">
        <div class="border-t md:col-span-2 pt-8">
          <div class="grid md:grid-cols-2 gap-8">
            <template v-if="config.public.site.allowPurchase">
              <SubscriptionsPaperCard
                v-for="paper in papers"
                :key="paper.RecordId"
                :paper="paper"
                :selected="selectedValidPapers.includes(paper.RecordId)"
                :has-access="userStore.user?.papers.includes(paper.RecordId)"
                :amount-per-year="5988"
                :discount="
                  selectedValidPapers.length &&
                  selectedValidPapers.indexOf(paper.RecordId) !== 0
                    ? 0.15
                    : 0
                "
                :in-waiting-period="
                  paperIdsInWaitingPeriod?.includes(paper.RecordId)
                "
                @select="handleSelect(paper)"
              />
            </template>
            <template v-else>
              <SubscriptionsPaperCardCompact
                v-for="paper in papers"
                :key="paper.RecordId"
                :paper="paper"
                :selected="selectedValidPapers.includes(paper.RecordId)"
                :has-access="userStore.user?.papers.includes(paper.RecordId)"
                :inWaitingPeriod="
                  paperIdsInWaitingPeriod?.includes(paper.RecordId)
                "
                @select="handleSelect(paper)"
              />
            </template>
          </div>
          <p
            v-if="
              selectedValidPapers.length && config.public.site.allowPurchase
            "
            class="text-base text-gray-800 mt-4"
          >
            * {{ $t('discountIsOnlyFirstYear') }}
          </p>
        </div>
        <div class="hidden md:block">
          <SubscriptionsOrderInfo
            :selected-valid-papers="selectedValidPapers"
            :selected-sorted-valid-papers="selectedSortedValidPapers"
            :papers="papers"
            :remove-selected-paper="removeSelectedPaper"
            :papersInWaitingPeriod="paperIdsInWaitingPeriod"
            @showCallMeModal="showCallMe = true"
          />
        </div>
        <LayoutModal v-if="showOrder" @modal-close="showOrder = false"
          ><SubscriptionsOrderInfo
            :selected-valid-papers="selectedValidPapers"
            :selected-sorted-valid-papers="selectedSortedValidPapers"
            :papers="papers"
            :remove-selected-paper="removeSelectedPaper"
            :papersInWaitingPeriod="paperIdsInWaitingPeriod"
            @showCallMeModal="showCallMe = true"
        /></LayoutModal>
      </div>
    </div>
    <div
      class="fixed md:hidden bottom-0 w-full h-20 bg-white p-4 z-[60] shadow-[0_-4px_6px_-1px_rgb(0_0_0_/_0.1),_0_-2px_4px_-2px_rgb(0_0_0_/_0.1)]"
    >
      <CommonButton
        border="border-none"
        bg="bg-blue"
        text="text-white"
        text-hover="hover:text-white"
        bg-hover="hover:bg-denimblue"
        class="w-full !h-full"
        @click="showOrder = true"
        >{{ $t('showOrder') }}</CommonButton
      >
    </div>
    <LayoutModal
      v-if="showCallMeConfirmation && showCallMe"
      @modal-close="
        () => {
          showCallMe = false
          showCallMeConfirmation = false
        }
      "
    >
      <CommonConfirmation
        @modal-close="
          () => {
            showCallMe = false
            showCallMeConfirmation = false
          }
        "
      >
        <h3 class="font-sans text-3xl font-semibold">
          {{ $t('BecomeSubscriberStep4') }}
        </h3>
        <p class="pt-3 mx-auto text-base leading-6 text-black text-opacity-50">
          {{ $t('trialSignup.callMeFormDetails') }}
        </p>
      </CommonConfirmation>
    </LayoutModal>
    <LayoutModal
      v-else-if="showCallMe"
      title="CallMe"
      @modal-close="showCallMe = false"
    >
      <AtsCallMe @complete="showCallMeConfirmation = true" />
    </LayoutModal>
  </div>
</template>

<script setup lang="ts">
import type { Paper } from '~/typesAuto/apicore/v1'
import type { ContentPaper } from '~/typesManual/content_api/paper'
import type { EcommerceItem } from '~/typesManual/dataLayer'

const nuxtApp = useNuxtApp()
const config = useRuntimeConfig()
const indexStore = useIndexStore()
const userStore = useUserStore()
const dataLayer = useDataLayer()

const showOrder = ref(false) // Only relevant on mobile. Always shown on desktop

const showCallMe = ref(false)
const showCallMeConfirmation = ref(false)

let selectedPapers = ref<number[]>([])

function handleSelect(paper: ContentPaper) {
  if (selectedPapers.value.some((p) => p === paper.RecordId)) {
    removeSelectedPaper(paper)
    return
  }

  dataLayer.ecommerce.addToCart({
    item_id: paper.Product?.[0]?.RecordId,
    item_name: paper.Product?.[0]?.Name,
  })

  selectedPapers.value.push(paper.RecordId)
}

function removeSelectedPaper(paper: ContentPaper) {
  selectedPapers.value = selectedPapers.value.filter(
    (currentPaperId) => currentPaperId !== paper.RecordId
  )

  dataLayer.ecommerce.removeFromCart({
    item_id: paper.Product?.[0]?.RecordId,
    item_name: paper.Product?.[0]?.Name,
  })
}

const papers = computed(() =>
  indexStore.papers.filter(
    (paper) => paper.RecordId !== 1 && paper.Status !== 0
  )
)

const selectedValidPapers = computed(() =>
  selectedPapers.value.filter(
    (currentPaperId) => !userStore.user?.papers.includes(currentPaperId)
  )
)

const selectedSortedValidPapers = computed(() =>
  [...selectedValidPapers.value].sort((a, b) => {
    const aName = papers.value.find((p) => p.RecordId === a)?.Name ?? ''
    const bName = papers.value.find((p) => p.RecordId === b)?.Name ?? ''
    return aName.localeCompare(bName)
  })
)

const asyncDataKey = `subscriptionPage-isLoggedIn-${userStore.isLoggedIn}`
const {
  data: paperIdsInWaitingPeriod,
  pending,
  refresh,
} = await useAsyncData(asyncDataKey, async () => {
  if (
    !userStore.isLoggedIn ||
    userStore.user?.loginType === 'IP' ||
    !userStore.user
  )
    return []

  const userPapers: Paper[] = await nuxtApp.$api.user.papers(
    userStore.user.userId
  )

  return userPapers
    .filter((paper) => paper.waitingPeriod === true)
    .map((paper) => paper.id)
})

onMounted(() => {
  dataLayer.ecommerce.viewItemList(
    'subscription',
    papers.value.map((p) => ({
      item_id: p.Product?.[0]?.RecordId,
      item_name: p.Product?.[0]?.Name,
    })) as EcommerceItem[]
  )
})

watch(
  () => userStore.isLoggedIn,
  () => {
    refresh()
  }
)

useHead({
  title: `${nuxtApp.$t('Subscription')} - ${
    config.public.site.meta.title.postfix.long
  }`,
})
</script>
